import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/inputs';
import {
  BANSHEE_TERMS,
  DEMON_TERMS,
  MAX_CONNECTIONS,
  PAYMENT_METHODS,
  PLANS,
  WRAITH_TERMS,
  money,
  quote,
} from '@/shared/pricing';
import { loadProducts } from '@/shared/catalog';
import { builtInState } from '@/shared/products';
import { GLOW, LABEL } from '@/components/home/styles';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { P, around } from '@/shared/locales/pages/keys';

/** How each plan is sold: its packages, how many screens, and the default checkout package. */
const SPEC = {
  Wraith: { terms: WRAITH_TERMS, maxConns: 1, screens: P.oneScreen, cta: 'Summon the Wraith', defaultTerm: '12m' },
  Demon: { terms: DEMON_TERMS, maxConns: MAX_CONNECTIONS, screens: P.oneToFive, cta: 'Summon the Demon', defaultTerm: '1y' },
  Banshee: {
    terms: BANSHEE_TERMS,
    maxConns: MAX_CONNECTIONS,
    screens: P.oneToFive,
    cta: P.summonB,
    defaultTerm: '1y',
  },
};

/** Package lengths by month count. These are the same keys the plan cards use. */
const LENGTH_LABELS = { 1: '1 Month', 3: '3 Months', 6: '6 Months', 12: '1 Year', 24: '2 Years', 36: '3 Years' };
const lengthKey = (months) => LENGTH_LABELS[months] ?? `${months} Months`;

const perMonth = (price, months) => Math.round((price / months) * 100) / 100;

const checkoutLink = (plan, term, conns) => `/checkout?plan=${plan}&term=${term}&connections=${conns}`;

/** Lowest monthly cost a plan reaches at one screen (its longest package). */
function fromPerMonth(plan) {
  const list = SPEC[plan].terms.map((x) => perMonth(quote(plan, x.id, 1), x.months));
  return Math.min(...list);
}

/**
 * The plans from `keys` that are on sale on the site. Demon and Wraith follow the
 * "On front page" switch in the product catalog, like the home page; Banshee always shows.
 */
export function useComparePlans(keys) {
  const { data, error } = useFetch(loadProducts, []);
  const ready = data !== undefined || !!error;
  const products = data ?? [];
  const plans = keys.filter((k) => k === 'Banshee' || builtInState(products, k).onHome);
  return { plans, ready };
}

function ScreensPicker({ conns, setConns, plans }) {
  const { t } = useT();
  const single = plans.filter((p) => SPEC[p].maxConns === 1);
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
      <p className={LABEL}>{t(P.screensSame)}</p>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: MAX_CONNECTIONS }, (_, i) => i + 1).map((n) => (
          <Pill key={n} on={conns === n} onClick={() => setConns(n)}>
            {n}
          </Pill>
        ))}
      </div>
      {conns > 1 && single.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {t(P.oneOnly, { name: single.map((p) => PLANS[p].name).join(' & ') })}
        </p>
      )}
    </div>
  );
}

function PlanHeads({ plans }) {
  const { t } = useT();
  const [pre, post] = around(t(P.fromPerMonth), 'price');
  return (
    <div className={cn('grid gap-8', plans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2')}>
      {plans.map((p) => (
        <div key={p} className="border-t border-border pt-8">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="font-heading text-5xl leading-none md:text-6xl">{PLANS[p].name}</h2>
            <span className={cn(LABEL, p === 'Demon' && 'text-destructive')}>{t(SPEC[p].screens)}</span>
          </div>
          <p className="mt-3 text-lg text-foreground/85">{t(PLANS[p].tagline)}</p>
          <p className="mt-2 text-[15px] leading-7 text-muted-foreground">{t(PLANS[p].blurb)}</p>
          <p className="mt-5 text-sm text-muted-foreground">
            {pre}
            <span className={cn('text-2xl font-light', GLOW)}>{money(fromPerMonth(p))}</span>
            {post}
          </p>
        </div>
      ))}
    </div>
  );
}

function PriceCell({ plan, months, conns }) {
  const { t } = useT();
  const spec = SPEC[plan];
  const term = spec.terms.find((x) => x.months === months);
  if (!term) {
    return (
      <span className="text-muted-foreground">
        <span aria-hidden="true">—</span>
        <span className="sr-only">{t(P.notOffered)}</span>
      </span>
    );
  }
  if (conns > spec.maxConns) {
    return <span className="text-sm text-muted-foreground">{t(P.oneScreenOnly)}</span>;
  }
  const price = quote(plan, term.id, conns);
  return (
    <Link
      to={checkoutLink(plan, term.id, conns)}
      aria-label={`${PLANS[plan].name}, ${t(lengthKey(months))}, ${t(P.screensN, { count: conns })}, ${money(price)}`}
      className="group inline-flex flex-col items-end rounded-md px-2 py-1.5 text-end transition-colors hover:bg-accent hover:text-accent-foreground"
    >
      <span className="text-base tabular-nums text-foreground group-hover:text-accent-foreground">{money(price)}</span>
      <span className="text-xs text-muted-foreground group-hover:text-accent-foreground/80">
        ≈ {t('{price}/mo', { price: money(perMonth(price, months)) })}
      </span>
      {term.epg && (
        <span className="mt-1 rounded-full border border-highlight/40 bg-highlight/15 px-2 py-0.5 text-xs text-highlight">
          + EPG
        </span>
      )}
    </Link>
  );
}

function PriceTable({ plans, conns }) {
  const { t } = useT();
  const lengths = [...new Set(plans.flatMap((p) => SPEC[p].terms.map((x) => x.months)))].sort((a, b) => a - b);
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className={cn('w-full text-sm', plans.length === 3 ? 'min-w-[620px]' : 'min-w-[480px]')}>
        <caption className="sr-only">
          {t(P.screensN, { count: conns })}, USD
        </caption>
        <thead className="bg-muted/50 text-muted-foreground">
          <tr>
            <th scope="col" className="px-4 py-3 text-start font-medium">
              {t(P.pkg)}
            </th>
            {plans.map((p) => (
              <th key={p} scope="col" className="px-4 py-3 text-end font-medium">
                {PLANS[p].name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lengths.map((m) => (
            <tr key={m} className="border-t border-border">
              <th scope="row" className="px-4 py-3 text-start font-normal">
                {t(lengthKey(m))}
              </th>
              {plans.map((p) => (
                <td key={p} className="px-2 py-2 text-end align-top">
                  <PriceCell plan={p} months={m} conns={conns} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Yes({ children }) {
  return (
    <span className="inline-flex items-center gap-2">
      <ApperIcon name="Check" size={16} className="text-primary" />
      {children ?? <span className="sr-only">✓</span>}
    </span>
  );
}

function No() {
  return (
    <span className="text-muted-foreground">
      <span aria-hidden="true">—</span>
    </span>
  );
}

const BEST_FOR = {
  Wraith: P.bestW,
  Demon: P.bestD,
  Banshee: P.bestB,
};

/** Per-plan details (Wraith runs on Strong 8K, Demon on V12 OTT). Banshee keeps its earlier, general entries. */
const DETAILS = {
  Wraith: { channels: '46,000+', vod: '180,000+' },
  Demon: { channels: '23,000+', vod: '147,000+' },
};

/** Feature rows. `cell` receives the plan and the translate function. */
const FEATURE_ROWS = [
  { label: P.bestFor, cell: (p, t) => t(BEST_FOR[p]) },
  { label: P.screensSame, cell: (p, t) => t(SPEC[p].screens) },
  {
    label: P.lengths,
    cell: (p, t) => {
      const terms = SPEC[p].terms;
      return t(P.toRange, { a: t(lengthKey(terms[0].months)), b: t(lengthKey(terms[terms.length - 1].months)) });
    },
  },
  { label: P.live, cell: (p) => (DETAILS[p] ? <Yes>{DETAILS[p].channels}</Yes> : <Yes />) },
  { label: P.vod, cell: (p) => (DETAILS[p] ? <Yes>{DETAILS[p].vod}</Yes> : <Yes />) },
  {
    label: P.quality,
    cell: (p, t) => (DETAILS[p] ? <Yes>{t(P.qualityVal)}</Yes> : <span className="text-muted-foreground">{t(P.askUs)}</span>),
  },
  {
    label: P.epg,
    cell: (p, t) =>
      p === 'Wraith' ? <Yes>{t(P.epgW)}</Yes> : p === 'Demon' ? <Yes>{t(P.epgD)}</Yes> : <No />,
  },
  {
    label: P.catchup,
    cell: (p, t) => (DETAILS[p] ? <Yes /> : <span className="text-muted-foreground">{t(P.askUs)}</span>),
  },
  {
    label: P.ppv,
    cell: (p, t) => (DETAILS[p] ? <Yes /> : <span className="text-muted-foreground">{t(P.askUs)}</span>),
  },
  { label: 'All supported devices', cell: () => <Yes /> },
  {
    label: P.who,
    only: 'Banshee',
    cell: (p, t) => (p === 'Banshee' ? t(P.invite) : t(P.anyone)),
  },
];

function FeatureTable({ plans }) {
  const { t } = useT();
  const rowsShown = FEATURE_ROWS.filter((r) => !r.only || plans.includes(r.only));
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className={cn('w-full text-sm', plans.length === 3 ? 'min-w-[620px]' : 'min-w-[480px]')}>
        <thead className="bg-muted/50 text-muted-foreground">
          <tr>
            <th scope="col" className="px-4 py-3 text-start font-medium">
              {t(P.whatYouGet)}
            </th>
            {plans.map((p) => (
              <th key={p} scope="col" className="px-4 py-3 text-start font-medium">
                {PLANS[p].name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowsShown.map((r) => (
            <tr key={r.label} className="border-t border-border">
              <th scope="row" className="px-4 py-3 text-start align-top font-normal">
                {t(r.label)}
              </th>
              {plans.map((p) => (
                <td key={p} className="px-4 py-3 align-top text-foreground/85">
                  {r.cell(p, t)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Ctas({ plans, conns }) {
  const { t } = useT();
  return (
    <div className={cn('grid gap-4', plans.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
      {plans.map((p) => {
        const spec = SPEC[p];
        const n = Math.min(conns, spec.maxConns);
        const term = spec.terms.find((x) => x.id === spec.defaultTerm);
        return (
          <div key={p} className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">
              {PLANS[p].name} · {t(lengthKey(term.months))} · {t(P.screensN, { count: n })}
            </p>
            <p className={cn('text-4xl font-light leading-none', GLOW)}>{money(quote(p, term.id, n))}</p>
            <Button className="mt-1 w-full" asChild>
              <Link to={checkoutLink(p, term.id, n)}>{t(spec.cta)}</Link>
            </Button>
            <p className="text-xs text-muted-foreground">{t(P.changeAt)}</p>
          </div>
        );
      })}
    </div>
  );
}

/** Side-by-side comparison of the given plans, priced for the chosen number of screens. */
export function PlanCompare({ plans, conns, setConns }) {
  const { t } = useT();
  return (
    <Fragment>
      <PlanHeads plans={plans} />

      <section className="mt-16" aria-labelledby="compare-prices">
        <h2 id="compare-prices" className="text-[clamp(28px,4vw,40px)] font-light leading-tight">
          {t(P.pricesSide)}
        </h2>
        <p className="mt-2 text-[15px] text-muted-foreground">{t(P.inUsd)}</p>
        <div className="mt-6">
          <ScreensPicker conns={conns} setConns={setConns} plans={plans} />
        </div>
        <div className="mt-6">
          <PriceTable plans={plans} conns={conns} />
        </div>
      </section>

      <section className="mt-16" aria-labelledby="compare-features">
        <h2 id="compare-features" className="text-[clamp(28px,4vw,40px)] font-light leading-tight">
          {t(P.included)}
        </h2>
        <div className="mt-6">
          <FeatureTable plans={plans} />
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          <span className={cn(LABEL, 'me-3')}>{t(P.everyPlan)}</span>
          {PAYMENT_METHODS.map((m) => t(m.label)).join('  ·  ')}
        </p>
      </section>

      <section className="mt-16" aria-labelledby="compare-order">
        <h2 id="compare-order" className="text-[clamp(28px,4vw,40px)] font-light leading-tight">
          {t(P.ready)}
        </h2>
        <div className="mt-6">
          <Ctas plans={plans} conns={conns} />
        </div>
      </section>
    </Fragment>
  );
}
